import { Box, Paper, Tab, Tabs } from '@mui/material';
import CardHeaderDTO from '@src/adapters/dto/card/CardHeaderDTO';
import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import DetailsTab from './tabs/details/DetailsTab';
import useGetCardLazy from '@src/adapters/hooks/card/useGetCard';
import CardDTO from '@src/adapters/dto/card/CardDTO';
import CardNoteDTO from '@src/adapters/dto/card/CardNoteDTO';
import ContentsTab from './tabs/contents/ContentsTab';
import CardItemDTO from '@src/adapters/dto/card/CardItemDTO';
import CardPreviewDialog from '@src/components/card/card-preview-dialog/CardPreviewDialog';
import { CardModificationDTO } from '@src/adapters/dto/card/CardModificationDTO';

interface Props {
  cardId: string;
  isEdit: boolean;
  previewOpen: boolean;
  onClosePreview(): void;
  onViewClick(): void;
  onHasModifications(): void;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}>
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  };
}

function CardInformation({
  cardId,
  isEdit,
  previewOpen,
  onClosePreview,
  onViewClick,
  onHasModifications,
}: Props) {
  const [value, setValue] = useState(0);
  const { execute: getCard } = useGetCardLazy();
  const [card, setCard] = useState<CardDTO>();
  const methods = useForm<CardHeaderDTO>();

  useEffect(() => {
    const search = async () => {
      if (cardId) {
        getCard({ id: cardId }).then((result) => {
          setCard(result.card);
        });
      }
    };
    search();
  }, [getCard, cardId]);

  const onChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  const handleNoteUpdated = (note: CardNoteDTO) => {
    setCard((prev) => {
      const notes = prev!.notes ?? [];
      return {
        ...prev!,
        notes: notes.some((n) => n.id === note.id)
          ? notes.map((n) => (n.id === note.id ? note : n))
          : [...notes, note],
      };
    });
  };

  const handleNoteDeleted = (noteId: string) => {
    setCard((prev) => {
      const notes = prev!.notes ?? [];
      return {
        ...prev!,
        notes: notes.filter((n) => n.id !== noteId),
      };
    });
    onHasModifications();
  };

  const handleItemDeleted = (id: string, modification: CardModificationDTO) => {
    setCard((prev) => {
      const items = prev!.items ?? [];
      const modifications = prev!.modifications ?? [];
      modifications.push(modification);
      return {
        ...prev!,
        items: items.filter((n) => n.id !== id),
        modifications,
      };
    });
    onHasModifications();
  };

  const handleItemUpdated = (
    cardItem: CardItemDTO,
    modification: CardModificationDTO
  ) => {
    setCard((prev) => {
      const items =
        prev!.items.map((i) => (i.id === cardItem.id ? cardItem : i)) ?? [];

      const modifications = prev!.modifications ?? [];
      modifications.push(modification);

      return {
        ...prev!,
        items,
        modifications,
      };
    });
    onHasModifications();
  };

  const handleItemAdded = (
    cardItem: CardItemDTO,
    modification: CardModificationDTO
  ) => {
    setCard((prev) => {
      const items = prev!.items ?? [];
      items.push(cardItem);

      const modifications = prev!.modifications ?? [];
      modifications.push(modification);

      return {
        ...prev!,
        items,
        modifications,
      };
    });
    onHasModifications();
  };

  return (
    <>
      <Paper
        elevation={8}
        sx={(theme) => ({
          width: '480px',
          height: `calc(100vh - ${theme.spacing(30)})`,
          overflow: 'auto',
        })}>
        <FormProvider {...methods}>
          <Tabs
            value={value}
            onChange={onChange}
            aria-label="details"
            variant="fullWidth">
            <Tab label="Details" {...a11yProps(0)} />
            <Tab label="Contents" {...a11yProps(1)} />
          </Tabs>
          <CustomTabPanel value={value} index={0}>
            {card && (
              <DetailsTab
                card={card}
                onNoteUpdated={handleNoteUpdated}
                onNoteDeleted={handleNoteDeleted}
                isEdit={isEdit}
              />
            )}
          </CustomTabPanel>
          <CustomTabPanel value={value} index={1}>
            {card && (
              <ContentsTab
                card={card}
                isEdit={isEdit}
                onItemDeleted={handleItemDeleted}
                onItemUpdated={handleItemUpdated}
                onItemAdded={handleItemAdded}
                onViewClick={onViewClick}
              />
            )}
          </CustomTabPanel>
        </FormProvider>
      </Paper>
      {card && (
        <CardPreviewDialog
          card={card}
          onClose={onClosePreview}
          open={previewOpen}
        />
      )}
    </>
  );
}

export default CardInformation;
